import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { TouristGuidesIntegrationApiService } from './_api/tourist-guides-integration.api.service';

@Component({
	selector: 'app-tourist-guides-integration',
	standalone: true,
	imports: [ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule],
	templateUrl: './tourist-guides-integration.component.html',
	styleUrl: './tourist-guides-integration.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TouristGuidesIntegrationComponent {
	readonly touristGuideId = input.required<string>();

	readonly isLoading = signal(false);
	readonly isSaving = signal(false);

	readonly integrationForm = new FormGroup({
		gratyfikant_id: new FormControl<string | null>(null),
	});

	private readonly api = inject(TouristGuidesIntegrationApiService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly infobox = inject(InfoboxService);

	constructor() {
		effect(
			() => {
				const id = this.touristGuideId();
				if (!id) {
					return;
				}
				this.loadIntegration(id);
			},
			{ allowSignalWrites: true },
		);
	}

	save(): void {
		this.isSaving.set(true);
		this.api
			.saveIntegration(this.touristGuideId(), {
				gratyfikant_id: this.integrationForm.controls.gratyfikant_id.value,
			})
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.infobox.success('Zapisano');
			});
	}

	private loadIntegration(touristGuideId: string): void {
		this.isLoading.set(true);
		this.api
			.getIntegration(touristGuideId)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => {
				this.integrationForm.controls.gratyfikant_id.setValue(data.gratyfikant_id);
			});
	}
}
